import React, { useCallback, useRef, useState } from 'react';
import { DraggableCore } from 'react-draggable';

import type { ReactNode, RefObject } from 'react';
import type {
  DraggableCoreProps,
  DraggableData,
  DraggableEvent,
} from 'react-draggable';

export type DraggableEventHandler = (
  e: DraggableEvent,
  data: DraggableData,
  context: any,
) => any | false;

interface BoundsRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface DragState {
  boundsRect: BoundsRect | null;
  nodeRect: DOMRect | null;
  cumulativeX: number;
  cumulativeY: number;
}

export interface DraggableProps
  extends Omit<
    Partial<DraggableCoreProps>,
    'onStart' | 'onDrag' | 'onStop' | 'children'
  > {
  /** CSS selector (e.g. '#my-bounds') for the bounding container */
  bounds?: string;
  onStart: DraggableEventHandler;
  onDrag: DraggableEventHandler;
  onStop: DraggableEventHandler;
  children: (ref: RefObject<any>, context: any) => ReactNode;
}

/**
 * Clamp cumulative deltas so the dragged element stays within bounds.
 * Returns clamped deltaX/deltaY for this frame.
 */
function clampDelta(
  deltaX: number,
  deltaY: number,
  dragState: DragState,
): { clampedDeltaX: number; clampedDeltaY: number } {
  const { boundsRect, nodeRect, cumulativeX, cumulativeY } = dragState;

  if (!boundsRect || !nodeRect) {
    return { clampedDeltaX: deltaX, clampedDeltaY: deltaY };
  }

  // Calculate where the node would be after applying deltaX/deltaY
  const proposedLeft = nodeRect.left + cumulativeX + deltaX;
  const proposedRight = proposedLeft + nodeRect.width;
  const proposedTop = nodeRect.top + cumulativeY + deltaY;
  const proposedBottom = proposedTop + nodeRect.height;

  let clampedDeltaX = deltaX;
  let clampedDeltaY = deltaY;

  // Clamp horizontal
  if (proposedLeft < boundsRect.left) {
    clampedDeltaX = deltaX + (boundsRect.left - proposedLeft);
  } else if (proposedRight > boundsRect.right) {
    clampedDeltaX = deltaX - (proposedRight - boundsRect.right);
  }

  // Clamp vertical
  if (proposedTop < boundsRect.top) {
    clampedDeltaY = deltaY + (boundsRect.top - proposedTop);
  } else if (proposedBottom > boundsRect.bottom) {
    clampedDeltaY = deltaY - (proposedBottom - boundsRect.bottom);
  }

  return { clampedDeltaX, clampedDeltaY };
}

export const Draggable: React.FC<DraggableProps> = props => {
  const { bounds, ...coreProps } = props;
  const [context, setContext] = useState();
  const dragStateRef = useRef<DragState | null>(null);

  const nodeRef = useRef<HTMLElement>(null);

  const initBounds = useCallback(() => {
    if (!bounds || !nodeRef.current) {
      dragStateRef.current = null;
      return;
    }

    const boundsEl = document.querySelector(bounds);
    if (!boundsEl) {
      dragStateRef.current = null;
      return;
    }

    const boundsRect = boundsEl.getBoundingClientRect();
    const nodeRect = nodeRef.current.getBoundingClientRect();

    dragStateRef.current = {
      boundsRect: {
        left: boundsRect.left,
        top: boundsRect.top,
        right: boundsRect.right,
        bottom: boundsRect.bottom,
      },
      nodeRect,
      cumulativeX: 0,
      cumulativeY: 0,
    };
  }, [bounds]);

  return (
    <DraggableCore
      {...coreProps}
      nodeRef={nodeRef}
      onStart={(e, data) => {
        initBounds();
        const newContext = props.onStart(e, data, undefined);
        if (!newContext) {
          return newContext;
        }
        setContext(newContext);
      }}
      onDrag={(e, data) => {
        // Clamp deltas if bounds are active
        if (dragStateRef.current) {
          const { clampedDeltaX, clampedDeltaY } = clampDelta(
            data.deltaX,
            data.deltaY,
            dragStateRef.current,
          );
          // Track cumulative movement for subsequent clamp calculations
          dragStateRef.current.cumulativeX += clampedDeltaX;
          dragStateRef.current.cumulativeY += clampedDeltaY;

          // Mutate data in place (react-draggable pattern)
          data.deltaX = clampedDeltaX;
          data.deltaY = clampedDeltaY;
        }

        const newContext = props.onDrag(e, data, context);
        if (!newContext) {
          return newContext;
        }
        setContext(newContext);
      }}
      onStop={(e, data) => {
        dragStateRef.current = null;
        setContext(undefined);
        props.onStop(e, data, context);
      }}
      // Avoid global style recalculation on click/drag
      // https://github.com/react-grid-layout/react-grid-layout/pull/1660
      enableUserSelectHack={props.enableUserSelectHack ?? false}
    >
      {props.children(nodeRef, context)}
    </DraggableCore>
  );
};
